import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  MapPin,
  Calendar,
  Users,
  Dog,
  MessageSquare,
  Settings,
  Loader2,
  UserPlus,
  UserMinus,
  UserCheck,
  Clock,
  Send,
  Flag,
  Ban,
  MoreHorizontal,
  Crown,
  Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ReportDialog from '../components/ReportDialog';
import BlockUserDialog from '../components/BlockUserDialog';
import VerifiedBadge from '../components/VerifiedBadge';
import TravelCreditsWidget from '../components/TravelCreditsWidget';
import ClaimedListingsWidget from '../components/ClaimedListingsWidget';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { setPageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [friendStatus, setFriendStatus] = useState({ status: 'none' });
  const [friendRequestOpen, setFriendRequestOpen] = useState(false);
  const [friendMessage, setFriendMessage] = useState('');

  const isOwnProfile = currentUser?.username === username;

  useEffect(() => {
    fetchProfile();
  }, [username]);

  // Update page title when profile loads
  useEffect(() => {
    if (profile?.display_name || profile?.username) {
      setPageTitle(`${profile.display_name || profile.username}'s Profile`);
    }
  }, [profile]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, postsRes] = await Promise.all([
        axios.get(`${API}/users/${username}`),
        axios.get(`${API}/posts`)
      ]);
      setProfile(profileRes.data);
      
      // Fetch user's dogs
      try {
        const dogsRes = await axios.get(`${API}/users/${profileRes.data.id}/dogs`);
        setDogs(dogsRes.data);
      } catch (e) {
        setDogs([]);
      }
      
      // Filter posts by user
      setPosts(postsRes.data.filter(p => p.author_username === username));
      
      // Check friendship status and following
      if (currentUser && !isOwnProfile) {
        try {
          const [followingRes, friendStatusRes] = await Promise.all([
            axios.get(`${API}/users/${currentUser.id}/following`),
            axios.get(`${API}/friends/check/${profileRes.data.id}`)
          ]);
          setIsFollowing(followingRes.data.some(u => u.username === username));
          setFriendStatus(friendStatusRes.data);
        } catch (e) {
          // Ignore error
        }
      }
      
      // Fetch friends list if visible
      try {
        const friendsRes = await axios.get(`${API}/users/${profileRes.data.id}/friends`);
        setFriends(friendsRes.data);
      } catch (e) {
        setFriends([]);
      }

      // Fetch user's media albums (public + own private)
      try {
        const albumsRes = await axios.get(`${API}/albums`, {
          params: { owner_id: profileRes.data.id, limit: 50 },
        });
        setAlbums(albumsRes.data || []);
      } catch (e) {
        setAlbums([]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('User not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API}/users/${profile.id}/follow`);
        setIsFollowing(false);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count - 1 }));
        toast.success('Unfollowed');
      } else {
        await axios.post(`${API}/users/${profile.id}/follow`);
        setIsFollowing(true);
        setProfile(prev => ({ ...prev, followers_count: prev.followers_count + 1 }));
        toast.success('Following!');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/friends/request`, {
        to_user_id: profile.id,
        message: friendMessage
      });
      toast.success('Friend request sent!');
      setFriendRequestOpen(false);
      setFriendMessage('');
      setFriendStatus({ status: 'request_sent' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleAcceptRequest = async () => {
    try {
      await axios.post(`${API}/friends/requests/${friendStatus.request_id}/accept`);
      toast.success('Friend request accepted!');
      setFriendStatus({ status: 'friends' });
      fetchProfile();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleCancelRequest = async () => {
    try {
      await axios.delete(`${API}/friends/requests/${friendStatus.request_id}`);
      toast.success('Request cancelled');
      setFriendStatus({ status: 'none' });
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleRemoveFriend = async () => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;
    try {
      await axios.delete(`${API}/friends/${profile.id}`);
      toast.success('Friend removed');
      setFriendStatus({ status: 'none' });
      fetchProfile();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  const renderFriendButton = () => {
    if (!currentUser || isOwnProfile) return null;

    switch (friendStatus.status) {
      case 'friends':
        return (
          <Button
            variant="outline"
            onClick={handleRemoveFriend}
            className="flex items-center gap-2"
          >
            <UserCheck className="w-4 h-4 text-primary" />
            Friends
          </Button>
        );
      case 'request_sent':
        return (
          <Button
            variant="outline"
            onClick={handleCancelRequest}
            className="flex items-center gap-2"
          >
            <Clock className="w-4 h-4" />
            Request Sent
          </Button>
        );
      case 'request_received':
        return (
          <Button
            onClick={handleAcceptRequest}
            className="btn-primary flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Accept Request
          </Button>
        );
      default:
        return (
          <Button
            variant="outline"
            onClick={() => setFriendRequestOpen(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Add Friend
          </Button>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Profile Header */}
        <Card className="mb-8 overflow-hidden">
          <div className="h-32 bg-gradient-to-r from-primary/20 to-secondary/20" />
          <CardContent className="relative pt-0 pb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
              <Avatar className="w-32 h-32 border-4 border-card">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-4xl bg-primary text-primary-foreground">
                  {profile.display_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-3xl font-heading font-medium">{profile.display_name}</h1>
                      {profile.identity_verified && <VerifiedBadge size="md" />}
                      {profile.membership_tier === 'premium' && (
                        <Crown className="w-5 h-5 text-yellow-500" />
                      )}
                    </div>
                    <p className="text-muted-foreground">@{profile.username}</p>
                  </div>
                  
                  <div className="flex gap-3">
                    {isOwnProfile ? (
                      <Button variant="outline" onClick={() => navigate('/settings')} data-testid="edit-profile-btn">
                        <Settings className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    ) : (
                      <>
                        {renderFriendButton()}
                        <Button
                          variant={isFollowing ? 'outline' : 'default'}
                          className={!isFollowing ? 'btn-primary' : ''}
                          onClick={handleFollow}
                          disabled={followLoading}
                          data-testid="follow-btn"
                        >
                          {followLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isFollowing ? (
                            <>
                              <UserMinus className="w-4 h-4 mr-2" />
                              Unfollow
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Follow
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/messages?to=${profile.id}`)}
                          data-testid="message-btn"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Message
                        </Button>
                        
                        {/* Report/Block dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon" data-testid="profile-more-menu">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <ReportDialog
                              contentType="user"
                              contentId={profile.id}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="report-user-btn">
                                  <Flag className="w-4 h-4 mr-2" />
                                  Report User
                                </DropdownMenuItem>
                              }
                            />
                            <BlockUserDialog
                              userId={profile.id}
                              username={profile.username}
                              trigger={
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="block-user-btn">
                                  <Ban className="w-4 h-4 mr-2" />
                                  Block @{profile.username}
                                </DropdownMenuItem>
                              }
                              onBlocked={() => {
                                toast.success(`@${profile.username} has been blocked`);
                                navigate('/');
                              }}
                            />
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {profile.bio && (
              <p className="mt-6 text-muted-foreground max-w-2xl">{profile.bio}</p>
            )}

            <div className="flex flex-wrap gap-6 mt-6 text-sm text-muted-foreground">
              {profile.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {profile.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Joined {format(new Date(profile.created_at), 'MMMM yyyy')}
              </span>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-6">
              <div className="text-center">
                <div className="text-2xl font-heading font-medium">{profile.followers_count}</div>
                <div className="text-sm text-muted-foreground">Followers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-medium">{profile.following_count}</div>
                <div className="text-sm text-muted-foreground">Following</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-medium">{profile.friends_count || friends.length}</div>
                <div className="text-sm text-muted-foreground">Friends</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading font-medium">{profile.dogs_count || dogs.length}</div>
                <div className="text-sm text-muted-foreground">Dogs</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Tabs */}
        <Tabs defaultValue="posts" className="space-y-6">
          {/* Travel credits — only visible on your own profile, and
              auto-hides if you have no balance and no pending bookings */}
          {isOwnProfile && (
            <div className="max-w-md">
              <TravelCreditsWidget />
            </div>
          )}
          {isOwnProfile && (
            <div className="max-w-md">
              <ClaimedListingsWidget />
            </div>
          )}
          <TabsList>
            <TabsTrigger value="posts" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Posts ({posts.length})
            </TabsTrigger>
            <TabsTrigger value="dogs" className="flex items-center gap-2">
              <Dog className="w-4 h-4" />
              Dogs ({dogs.length})
            </TabsTrigger>
            <TabsTrigger value="friends" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Friends ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex items-center gap-2" data-testid="profile-photos-tab">
              <Camera className="w-4 h-4" />
              Photos ({albums.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No posts yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card 
                    key={post.id}
                    className="card-interactive cursor-pointer"
                    onClick={() => navigate(`/post/${post.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading font-medium">{post.title}</h3>
                        <Badge variant="secondary">{post.category}</Badge>
                      </div>
                      <p className="text-muted-foreground text-sm line-clamp-2">{post.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="dogs">
            {dogs.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Dog className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No dogs in profile</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {dogs.map((dog) => (
                  <Card key={dog.id} className="card-interactive overflow-hidden">
                    <div className="aspect-square bg-muted">
                      {dog.image_url ? (
                        <img 
                          src={dog.image_url}
                          alt={dog.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Dog className="w-16 h-16 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading font-medium">{dog.name}</h3>
                      {dog.breed && (
                        <p className="text-sm text-muted-foreground">{dog.breed}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="friends">
            {friends.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No friends yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {friends.map((friend) => (
                  <Card 
                    key={friend.id} 
                    className="card-interactive cursor-pointer"
                    onClick={() => navigate(`/profile/${friend.username}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={friend.avatar_url} />
                          <AvatarFallback>{friend.display_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{friend.display_name}</p>
                          <p className="text-sm text-muted-foreground">@{friend.username}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos">
            {albums.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Camera className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground mb-3">No photo albums yet</p>
                  {isOwnProfile && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/media-albums')}
                      data-testid="create-album-cta"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Create your first album
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-photo-albums">
                {albums.map((album) => (
                  <Card
                    key={album.id}
                    className="card-interactive cursor-pointer overflow-hidden"
                    onClick={() => navigate(`/album/${album.id}`)}
                  >
                    {album.cover_image_url ? (
                      <img
                        src={album.cover_image_url}
                        alt={album.title}
                        className="w-full h-40 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-40 bg-muted flex items-center justify-center">
                        <Camera className="w-10 h-10 text-muted-foreground/40" />
                      </div>
                    )}
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{album.title}</p>
                          {album.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">{album.description}</p>
                          )}
                        </div>
                        {!album.is_public && (
                          <Badge variant="outline" className="shrink-0 text-[10px]">Private</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {album.media_count || 0} photo{album.media_count === 1 ? '' : 's'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Friend Request Dialog */}
      <Dialog open={friendRequestOpen} onOpenChange={setFriendRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Send Friend Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-muted-foreground">
              Send a friend request to {profile.display_name}
            </p>
            <Textarea
              placeholder="Add a message (optional)"
              value={friendMessage}
              onChange={(e) => setFriendMessage(e.target.value)}
              rows={3}
            />
            <Button onClick={handleSendFriendRequest} className="w-full btn-primary">
              <Send className="w-4 h-4 mr-2" />
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
